import { useCallback, useMemo } from 'react'
import type { ReactNode } from 'react'
import { FormattedMessage } from 'react-intl'
import OfferCardCarouselSection from 'components/Common/OfferCardCarousel/OfferCardCarouselSection'
import useRegionAwareOfferList from 'hooks/Offers/useRegionAwareOfferList'
import { getSearchQueryFromFilter } from 'lib/search/searchUtils'
import { LUX_PLUS_MONTH_CAMPAIGN_ID } from '../constants/luxPlusMonthTakeoverConstants'

// Same "hidden offer" filter shape as the LuxPlus+ landing page's Hidden Offers tab
// (LuxPlusFilteredOfferCarousel's getFiltersForCategory('hidden-offers', ...)), but rendered
// through the standard, un-blurred OfferCardCarouselSection tile rather than the locked/blurred
// LuxPlusHiddenOfferCarouselCard treatment — these are shown fully visible to everyone during
// the campaign, members and non-members alike. Scoped to this campaign's tag, on top of the
// member-exclusive filter.
function LuxPlusMonthTakeoverHiddenOfferCarousel() {
  const renderItalic = useCallback((chunks: ReactNode) => <i>{chunks}</i>, [])

  const baseFilters = useMemo<Omit<App.OfferListFilters, 'destinationId' | 'limit'>>(() => ({
    offerTypes: ['hotel', 'tactical_ao_hotel'],
    campaigns: [LUX_PLUS_MONTH_CAMPAIGN_ID],
    luxPlusFeatures: ['Member exclusive'],
    scheduleVisibilities: ['luxPlusExclusiveVisible'],
  }), [])

  // No separate campaigns split here (unlike the best-hotel-offers carousel) — this carousel is
  // member-exclusive-only, so dropping the campaign tag as a backup/anywhere fallback isn't
  // appropriate. Region and anywhere requests share the same filters, campaign tag included.
  const offerList = useRegionAwareOfferList(baseFilters)

  const allLink = useMemo(() => `/search?${getSearchQueryFromFilter(baseFilters).toString()}`, [baseFilters])

  return (
    <OfferCardCarouselSection
      id="lux-plus-month-hotels"
      title={<FormattedMessage
        id="8TzlAaZOBt88pV8eDLdV"
        defaultMessage="<i>Exclusive</i> LuxPlus+ offers"
        description="Section title for the LuxPlus+ Month campaign landing page carousel showing member-exclusive hidden offers, unblurred, to all visitors, with 'Exclusive' shown in italics"
        values={{ i: renderItalic }}
      />}
      filters={baseFilters}
      overrideOfferList={offerList}
      allLink={allLink}
      hideWhenEmpty
      tileStyle="card-default"
      tileSize="medium"
    />
  )
}

export default LuxPlusMonthTakeoverHiddenOfferCarousel
